import { buildFocTourRequirementTaskListHtml } from 'components/MapPage/focTourRequirementChecklist';

export const FOC_CAPTURED_FEEDBACK_ATTR = 'data-foc-captured-feedback';
export const FOC_REQ_LINE_ATTR = 'data-foc-req';

export type CapturedFeedbackEntry = {
  reaction: string;
  text: string;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function unescapeAttr(value: string): string {
  return value.replace(/&quot;/g, '"').replace(/&amp;/g, '&');
}

function formatFeedbackLine(
  requirementKey: string,
  reaction: string,
  text: string
): string {
  const parts = [requirementKey + ':'];
  const trimmedReaction = reaction.trim();
  const trimmedText = text.trim();
  if (trimmedReaction) parts.push(trimmedReaction);
  if (trimmedText) parts.push(trimmedText);
  return parts.join(' ');
}

function emptyCapturedSection(): string {
  return [
    `<p><strong>Captured during tour</strong></p>`,
    `<div ${FOC_CAPTURED_FEEDBACK_ATTR}="1"></div>`,
  ].join('');
}

/** TipTap-friendly HTML body for a new FOC feedback note. */
export function focFeedbackNoteBodyHtml(tourLabel: string): string {
  const name = escapeHtml(tourLabel);
  const checklist = buildFocTourRequirementTaskListHtml(tourLabel);
  return [
    `<p><strong>FOC Feedback — ${name}</strong></p>`,
    '<p>Thanks for completing this product tour. Please try a couple of things from the walkthrough on your own, then capture your feedback in this note and click <strong>Save</strong>.</p>',
    '<ul>',
    '<li>Revisit one or two features you saw in the tour and note what worked well.</li>',
    '<li>Note anything confusing, missing, or that you would change.</li>',
    '<li>Add any other comments for the FOC / product team.</li>',
    '</ul>',
    emptyCapturedSection(),
    checklist,
    '<p><em>Write your feedback below this line.</em></p>',
    '<p></p>',
  ]
    .filter(Boolean)
    .join('');
}

/**
 * Replace or insert one captured-feedback line inside the marked section.
 * Creates the section if the body was seeded before this feature existed.
 */
export function upsertCapturedFeedbackLine(
  bodyHtml: string,
  input: { requirementKey: string; reaction: string; text: string }
): string {
  const key = input.requirementKey.trim();
  if (!key) return bodyHtml;

  const empty = !input.reaction.trim() && !input.text.trim();
  const lineText = escapeHtml(
    formatFeedbackLine(key, input.reaction, input.text)
  );
  const lineHtml = empty
    ? ''
    : `<p ${FOC_REQ_LINE_ATTR}="${escapeAttr(key)}">${lineText}</p>`;

  const sectionRe = new RegExp(
    `<div[^>]*\\s${FOC_CAPTURED_FEEDBACK_ATTR}=["']1["'][^>]*>([\\s\\S]*?)<\\/div>`,
    'i'
  );
  const match = bodyHtml.match(sectionRe);
  const lineRe = new RegExp(
    `<p[^>]*\\s${FOC_REQ_LINE_ATTR}=["']${escapeAttr(key).replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&'
    )}["'][^>]*>[\\s\\S]*?<\\/p>`,
    'i'
  );

  if (!match) {
    if (empty) return bodyHtml;
    const checklistMarker = '<p><strong>Requirement status</strong>';
    const freeformMarker = '<p><em>Write your feedback below this line.</em></p>';
    const section = `${emptyCapturedSection().replace(
      `<div ${FOC_CAPTURED_FEEDBACK_ATTR}="1"></div>`,
      `<div ${FOC_CAPTURED_FEEDBACK_ATTR}="1">${lineHtml}</div>`
    )}`;
    if (bodyHtml.includes(checklistMarker)) {
      return bodyHtml.replace(checklistMarker, `${section}${checklistMarker}`);
    }
    if (bodyHtml.includes(freeformMarker)) {
      return bodyHtml.replace(freeformMarker, `${section}${freeformMarker}`);
    }
    return `${bodyHtml}${section}`;
  }

  const inner = match[1] ?? '';
  let nextInner: string;
  if (lineRe.test(inner)) {
    nextInner = empty ? inner.replace(lineRe, '') : inner.replace(lineRe, lineHtml);
  } else if (empty) {
    nextInner = inner;
  } else {
    nextInner = `${inner}${lineHtml}`;
  }

  return bodyHtml.replace(
    sectionRe,
    `<div ${FOC_CAPTURED_FEEDBACK_ATTR}="1">${nextInner}</div>`
  );
}

/** Parse captured step feedback lines from a note body into a map. */
export function parseCapturedFeedback(
  bodyHtml: string
): Map<string, CapturedFeedbackEntry> {
  const out = new Map<string, CapturedFeedbackEntry>();
  const lineRe = new RegExp(
    `<p[^>]*\\s${FOC_REQ_LINE_ATTR}=["']([^"']+)["'][^>]*>([\\s\\S]*?)<\\/p>`,
    'gi'
  );
  let match: RegExpExecArray | null;
  while ((match = lineRe.exec(bodyHtml)) !== null) {
    const key = unescapeAttr(match[1] ?? '').trim();
    if (!key) continue;
    const textContent = (match[2] ?? '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .trim();
    // "{key}: {reaction?} {text?}"
    const prefix = `${key}:`;
    const rest = textContent.startsWith(prefix)
      ? textContent.slice(prefix.length).trim()
      : textContent;
    const reactionMatch = rest.match(/^(\S+)\s*(.*)$/u);
    const maybeReaction = reactionMatch?.[1] ?? '';
    const isEmoji =
      maybeReaction.length > 0 &&
      !/[A-Za-z0-9]/.test(maybeReaction) &&
      maybeReaction.length <= 8;
    if (isEmoji) {
      out.set(key, {
        reaction: maybeReaction,
        text: (reactionMatch?.[2] ?? '').trim(),
      });
    } else {
      out.set(key, { reaction: '', text: rest });
    }
  }
  return out;
}
